import { FunctionComponent, useEffect, useState } from "react";
import { connect } from "react-redux";
import { Dispatch, RootState } from "core/store/store.types";
import {
  Autocomplete,
  Box,
  FormControl,
  Grid,
  LinearProgress,
  Paper,
  Snackbar,
  TextField,
  Alert as MuiAlert,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { authSelectors } from "core/store/modules/auth";
import { searchAsync, searchSelectors } from "core/store/modules/search";
import { User } from "core/types/user.types";
import { Result, ResultList, Search } from "core/types/search.types";
import { Alert } from "components/molecules/alert";
import { useNavigate } from "react-router-dom";
import { Text } from "components/atoms/text";
import { format } from "date-fns";
import { enGB, it } from "date-fns/locale";
import i18n from "i18n";
import { SearchDetail } from "containers/search-detail";
import {
  practiceActions,
  practiceAsync,
  practiceSelectors,
} from "core/store/modules/practice";
import { customOrange } from "core/theme/variants";
import TravelExploreRoundedIcon from "@mui/icons-material/TravelExploreRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";

interface OwnProps {}
interface StateProps {
  user?: User;
  resultList?: ResultList[];
  resultDetail?: Search;
  isSearchLoading: boolean;
  isPracticeLoading: boolean;
  errorPractice?: string;
  successPractice?: string;
}
interface DispatchProps {
  getSearchList: () => void;
  getSearchDetail: (id: string) => void;
  pollSearchDetail: (id: string) => void;
  resetDetail: () => void;
  createPractice: (urls: Result[]) => void;
  resetErrorPractice: () => void;
  resetSuccessPractice: () => void;
}

export type Props = OwnProps & StateProps & DispatchProps;

const Searches: FunctionComponent<Props> = ({
  user,
  resultList,
  resultDetail,
  isSearchLoading,
  isPracticeLoading,
  errorPractice,
  successPractice,
  getSearchList,
  getSearchDetail,
  pollSearchDetail,
  resetDetail,
  createPractice,
  resetErrorPractice,
  resetSuccessPractice,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [orderedList, setOrderedList] = useState<ResultList[]>([]);
  const [selectedResearch, setSelectedResearch] = useState<string>("");
  const linguaCorrente = i18n.language;
  const [openSnackbar, setOpenSnackbar] = useState(false);

  useEffect(() => {
    resetDetail();
    getSearchList();
  }, []);

  useEffect(() => {
    if (resultList) {
      setOrderedList(
        resultList.sort(
          (a, b) =>
            new Date(b.searchCreationTime).getTime() -
            new Date(a.searchCreationTime).getTime()
        )
      );
    }
  }, [resultList]);

  useEffect(() => {
    if (selectedResearch) {
      resetDetail();
      getSearchDetail(selectedResearch);
    }
  }, [selectedResearch]);

  // Polling progressivo: ricarica il dettaglio in silenzio ogni 3.5s finche' la
  // ricerca non e' conclusa (status === 'done' E tutti i sentiment caricati).
  // Polla sull'ID SELEZIONATO (non su resultDetail.id) cosi' funziona anche nei
  // primi secondi quando il dettaglio e' ancora vuoto (0 risultati): l'utente vede
  // "Stiamo analizzando..." e i risultati crescere live. Si ferma su 'done' o al
  // cambio ricerca/smontaggio.
  useEffect(() => {
    if (!selectedResearch) return;
    const status = resultDetail?.status;
    const sentimentsLoading = (resultDetail?.results || []).some(
      (r) => !r.sentiment
    );
    const finished =
      !!resultDetail?.id && status === "done" && !sentimentsLoading;
    if (finished) return;
    const interval = setInterval(() => {
      pollSearchDetail(selectedResearch);
    }, 3500);
    return () => clearInterval(interval);
  }, [selectedResearch, resultDetail, pollSearchDetail]);

  const handleGeneratePractice = (urls: Result[]) => {
    createPractice(urls);
  };

  useEffect(() => {
    if (successPractice !== "" && successPractice !== undefined) {
      setOpenSnackbar(true);
    }
  }, [successPractice]);

  useEffect(() => {
    if (errorPractice) {
      setOpenSnackbar(true);
    }
  }, [errorPractice]);

  return (
    <>
      {user?.billingInfo?.searchesCount === 0 && (
        <Alert
          title={t("alert.title")}
          subtitle={t("alert.subtitle")}
          buttonTitle={t("alert.button")}
          buttonAction={() => navigate("/my-profile")}
        />
      )}
      <Text fontSize={"16px"} fontWeight={"700"} paddingY={"15px"}>
        {t("searches.title")}
      </Text>

      <Grid container spacing={6}>
        <Grid item xs={12} sm={5}>
          <FormControl variant="standard" fullWidth>
            <Autocomplete
              id="autocomplete-research"
              value={
                orderedList.find(
                  (ol) => ol.searchId.toString() === selectedResearch
                ) || null
              }
              onChange={(event, newValue) => {
                setSelectedResearch(
                  newValue ? newValue.searchId.toString() : ""
                );
              }}
              options={orderedList}
              getOptionLabel={(option) =>
                format(
                  new Date(option.searchCreationTime),
                  "dd MMM yyyy HH:mm",
                  {
                    locale: linguaCorrente === "it" ? it : enGB,
                  }
                )
              }
              renderOption={(props, option) => (
                <li {...props} key={option.searchId}>
                  {format(
                    new Date(option.searchCreationTime),
                    "dd MMM yyyy HH:mm",
                    {
                      locale: linguaCorrente === "it" ? it : enGB,
                    }
                  )}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("searches.title")}
                  InputLabelProps={{
                    sx: { fontSize: "14px !important", fontWeight: "700" },
                  }}
                  variant="standard"
                  InputProps={{
                    ...params.InputProps,
                  }}
                />
              )}
              loading={isSearchLoading}
              noOptionsText={t("empty_states.loading")}
            />
          </FormControl>
        </Grid>
        {selectedResearch &&
          (() => {
            const liveResults = resultDetail?.results || [];
            const sentimentsLoading = liveResults.some((r) => !r.sentiment);
            const isDone =
              resultDetail?.status === "done" && !sentimentsLoading;
            return (
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    backgroundColor: customOrange[50],
                    border: "1px solid",
                    borderColor: isDone ? "primary.main" : customOrange[200],
                    borderRadius: "14px",
                    padding: "18px 22px",
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    gap="12px"
                  >
                    <Box display="flex" alignItems="center" gap="12px">
                      {isDone ? (
                        <CheckCircleRoundedIcon color="primary" />
                      ) : (
                        <TravelExploreRoundedIcon
                          sx={{ color: customOrange[1000] }}
                        />
                      )}
                      <Box>
                        <Text variant="h3" fontSize="18px" fontWeight="800">
                          {isDone
                            ? t("new-search.results.done_title")
                            : t("new-search.results.progress_title")}
                        </Text>
                        <Text
                          fontSize="13px"
                          fontWeight="600"
                          sx={{ color: "#5b6472" }}
                        >
                          {isDone
                            ? t("new-search.results.done_count", {
                                count: liveResults.length,
                              })
                            : t("new-search.results.progress_subtitle")}
                        </Text>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        backgroundColor: "#fff",
                        border: "1px solid",
                        borderColor: isDone
                          ? "primary.main"
                          : customOrange[200],
                        borderRadius: "999px",
                        padding: "6px 16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Text
                        fontSize="15px"
                        fontWeight="800"
                        sx={{ color: customOrange[1000] }}
                      >
                        {liveResults.length}
                      </Text>
                      <Text
                        fontSize="13px"
                        fontWeight="600"
                        sx={{ color: "#1D2C54" }}
                      >
                        {liveResults.length === 0
                          ? t("new-search.results.progress_count_zero")
                          : t("new-search.results.progress_count", {
                              count: liveResults.length,
                            })}
                      </Text>
                    </Box>
                  </Box>
                  {!isDone && (
                    <LinearProgress
                      color="primary"
                      variant="indeterminate"
                      sx={{
                        height: "6px",
                        width: "100%",
                        borderRadius: "999px",
                        backgroundColor: customOrange[100],
                        marginTop: "16px",
                      }}
                    />
                  )}
                </Paper>
              </Grid>
            );
          })()}
        <Grid item xs={12} display={"inline-block"} maxWidth={"100%"}>
          {resultDetail?.id && (
            <SearchDetail
              selectedResearch={selectedResearch}
              isResearchLoading={isSearchLoading}
              researchDetail={resultDetail}
              generatePractice={handleGeneratePractice}
              isPracticeLoading={isPracticeLoading}
              userRole={user?.role}
              getSearches={() => {
                if (selectedResearch) {
                  resetDetail();
                  getSearchDetail(selectedResearch);
                }
              }}
            />
          )}
        </Grid>
      </Grid>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        anchorOrigin={{ horizontal: "center", vertical: "top" }}
        onClose={() => {
          setOpenSnackbar(false);
          resetErrorPractice();
          resetSuccessPractice();
        }}
      >
        <MuiAlert
          onClose={() => {
            setOpenSnackbar(false);
            resetErrorPractice();
            resetSuccessPractice();
          }}
          severity={errorPractice ? "error" : "success"}
          sx={{ width: "100%", borderRadius: "10px" }}
        >
          {errorPractice
            ? errorPractice
            : t("report.generate_practice_success")}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default connect<StateProps, DispatchProps, OwnProps, RootState>(
  (state: RootState) => ({
    isSearchLoading: searchSelectors.isSearchLoading(state),
    isPracticeLoading: practiceSelectors.isPracticeLoading(state),
    user: authSelectors.getUser(state),
    resultList: searchSelectors.getResultList(state),
    resultDetail: searchSelectors.getResultDetail(state),
    errorPractice: practiceSelectors.getPracticeError(state),
    successPractice: practiceSelectors.getPracticeSuccess(state),
  }),
  (dispatch: Dispatch) => ({
    getSearchList: () => dispatch(searchAsync.getSearches()),
    getSearchDetail: (id: string) => dispatch(searchAsync.getSearchDetail(id)),
    pollSearchDetail: (id: string) =>
      dispatch(searchAsync.pollSearchDetail(id)),
    resetDetail: () => dispatch(searchAsync.resetDetail()),
    createPractice: (urls: Result[]) =>
      dispatch(practiceAsync.newPractice(urls)),
    resetErrorPractice: () => dispatch(practiceActions.error({ error: "" })),
    resetSuccessPractice: () =>
      dispatch(practiceActions.success({ success: "" })),
  })
)(Searches);
